import {
    ApplicationRef,
    ElementRef,
    EmbeddedViewRef,
    Injectable,
    Injector,
    ComponentFactoryResolver
} from '@angular/core';

// services
import { RendererService } from './rendererService';
import { PACKAGE_ROOT_URL } from '@angular/core/src/application_tokens';

@Injectable()
export class OverlayService {
    visible: boolean;
    overlayWrapper: HTMLElement;
    overlays: Node[];
    tooltips: Node[];


    constructor(
        private _appRef: ApplicationRef,
        private _componentFactoryResolver: ComponentFactoryResolver,
        private _injector: Injector,
        private _renderer: RendererService
    ) {
        this.overlays = [];
        this.tooltips = [];
        // Create a div
        this.overlayWrapper = this._renderer.renderer2.createElement("div");
        // Add class "overlay-wrapper"
        this.overlayWrapper.classList.add('overlay-wrapper');
    }

    checkVisible(): void {
        if (
            this.visible &&
            this.overlays.length === 0 &&
            this.tooltips.length === 0
        ) {
            // make sure we remove the overlay since no more overlays are visible
            document.body.removeChild(this.overlayWrapper);
            this.visible = false;
        } else if (
            !this.visible &&
            (
                this.overlays.length > 0 ||
                this.tooltips.length > 0
            )
        ) {
            // append the overlay to the body
            document.body.appendChild(this.overlayWrapper);
            this.visible = true;
        }
    }

    showTooltip(elementRef: ElementRef): void {
        if (this.tooltips.indexOf(elementRef.nativeElement) === -1) {
            if (this.tooltips.length > 0) {
                // remove the current tooltip in the overlay wrapper
                this.overlayWrapper.removeChild(this.tooltips[this.tooltips.length - 1]);
            }
            // add the overlay wrapper, just before the input
            this.overlayWrapper.appendChild(elementRef.nativeElement);
            this.tooltips.push(elementRef.nativeElement);
        }
        this.checkVisible();
    }

    removeTooltip(elementRef: ElementRef): void {
        if (this.tooltips.indexOf(elementRef.nativeElement) !== -1) {
            // remove from overlay if this tooltip is currently shown
            if (this.tooltips.indexOf(elementRef.nativeElement) === this.tooltips.length - 1) {
                this.overlayWrapper.removeChild(elementRef.nativeElement);
            }
            this.tooltips.splice(this.tooltips.indexOf(elementRef.nativeElement), 1);
            if (this.tooltips.length > 0) {
                // add the overlay wrapper, just before the input
                this.overlayWrapper.appendChild(this.tooltips[this.tooltips.length - 1]);
            }
        }
        this.checkVisible();
    }

    showOverlayItem(elementRef: ElementRef): void {
        if (this.overlays.indexOf(elementRef.nativeElement) === -1) {
            // add the overlay wrapper, just before the input
            this.overlayWrapper.appendChild(elementRef.nativeElement);
            this.overlays.push(elementRef.nativeElement);
        }

        this.checkVisible();
    }

    removeOverlayItem(elementRef: ElementRef): void {
        if (this.overlays.indexOf(elementRef.nativeElement) !== -1) {
            // remove the overlay wrapper, just before the input
            this.overlayWrapper.removeChild(elementRef.nativeElement);
            this.overlays.splice(this.overlays.indexOf(elementRef.nativeElement), 1);
        }

        this.checkVisible();
    }

    removeAllOverlayItems(): void {
        this.overlays.forEach(element => {
            this.overlayWrapper.removeChild(element);
        });
        this.overlays = [];
    }

    /**
     * Creates a component which attaches itself to the document body
     * @param component The component class to be created and attached
     * @returns The function to dispose the created component
     */
    appendComponentToBody(component: any): { componentRef: any, dispose: Function } {
        // 1. Create a component reference from the component 
        const componentRef = this._componentFactoryResolver
            .resolveComponentFactory(component)
            .create(this._injector);

        // 2. Attach component to the appRef so that it's inside the ng component tree
        this._appRef.attachView(componentRef.hostView);

        // 3. Get DOM element from component
        const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
            .rootNodes[0] as HTMLElement;

        // 4. Append DOM element to the body
        document.body.appendChild(domElem);

        // 5. Wait some time and remove it from the component tree and from the DOM
        return {
            componentRef: componentRef,
            dispose: (): void => {
                this._appRef.detachView(componentRef.hostView);
                componentRef.destroy();
            }
        }
    }
}



// WEBPACK FOOTER //
// ./client-src/app/core/services/overlay.service.ts